import { useMemo } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchLoadedSessionId } from "@/tools/helper";

export interface UseLoadedSessionIdResult {
  sessionId?: string;
  error?: Error;
  isLoading?: boolean;
  hasData: boolean;
}

/**
 * Hook for managing loaded session ID
 *
 * @description Fetches session ID from the pg/load endpoint when loading a saved configuration.
 *
 * @param id - Optional configuration ID to load from the server
 * @returns Object containing:
 *   - sessionId: The loaded session ID (from persistent cache or current request)
 *   - error: Any error that occurred during the request
 *   - isLoading: Loading state of the request
 *   - hasData: Boolean indicating if there's any loaded session data
 *
 * @example
 * ```typescript
 * const { sessionId, error, isLoading, hasData } = useLoadedSessionId("config-123");
 * ```
 */
export const useLoadedSessionId = (
  id?: string | undefined,
): UseLoadedSessionIdResult => {
  const queryClient = useQueryClient();

  const {
    data: fetchedSessionId,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["loadedSessionId", id],
    queryFn: async () => {
      try {
        const result = await fetchLoadedSessionId(id);
        if (result) {
          queryClient.setQueryData(["loadedSessionData"], {
            sessionId: result,
            timestamp: Date.now(),
          });
          queryClient.removeQueries({ queryKey: ["resetSessionData"] });
        }
        return result;
      } catch (error) {
        console.error("Failed to load session ID:", error);
        throw error;
      }
    },
    enabled: !!id,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: (failureCount, error) => {
      if (failureCount >= 3) return false;
      if (error instanceof Error && error.message.includes("404")) return false;
      return true;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { data: persistedData } = useQuery({
    queryKey: ["loadedSessionData"],
    queryFn: () => queryClient.getQueryData(["loadedSessionData"]),
    enabled: true,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const sessionId = useMemo(() => {
    const persisted = persistedData as
      | { sessionId: string; timestamp: number }
      | undefined;
    return persisted?.sessionId || fetchedSessionId;
  }, [persistedData, fetchedSessionId]);

  const hasData = useMemo(() => {
    const persisted = persistedData as
      | { sessionId: string; timestamp: number }
      | undefined;
    return !!(persisted?.sessionId || fetchedSessionId);
  }, [persistedData, fetchedSessionId]);

  return {
    sessionId,
    error: error ?? undefined,
    isLoading,
    hasData,
  };
};
